// CommonRangePicker.tsx - Reusable date range picker component
import React, { useEffect, useState, useMemo } from 'react';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import './CommonRangePicker.css';

const { RangePicker } = DatePicker;

interface CommonRangePickerProps {
  // Current lead filter compatibility props
  filterData?: any;
  dateFilterOnChange?: (value: any, name: string) => void;
  startDate?: (date: any) => void;
  endDate?: (date: any) => void;
  showDatePicker?: boolean;
  
  // Or direct range picker props for other use cases
  value?: [Moment | null, Moment | null] | null;
  onChange?: (dates: any, dateStrings: [string, string]) => void;
  
  // Configuration
  presetType?: 'lead-filter' | 'distribution' | 'basic';
  containerId?: string;
  placeholder?: [string, string];
  className?: string;
  allowClear?: boolean;
  
  // For debugging
  debugMode?: boolean;
}

const CommonRangePicker: React.FC<CommonRangePickerProps> = ({
  // Lead filter compatibility props
  filterData,
  dateFilterOnChange,
  startDate,
  endDate,
  
  // Direct props
  value,
  onChange,
  
  // Configuration
  presetType = 'lead-filter',
  containerId = 'common-range-picker-container',
  placeholder = ['Start Date', 'End Date'],
  className = '',
  allowClear = true,
  debugMode = false
}) => {
  
  // Internal state for managing the range picker
  const [internalRange, setInternalRange] = useState<[Moment | null, Moment | null] | null>(null);
  
  // Console logging for debugging dates (because dates are a bitch)
  const log = (message: string, data?: any) => {
    if (debugMode) {
      console.log(`🗓️ CommonRangePicker: ${message}`, data);
    }
  };
  

  
  // Get current value - either from props or internal state
  const getCurrentValue = (): [Moment | null, Moment | null] | null => {
    // If using direct value prop
    if (value !== undefined) {
      return value;
    }
    
    // If using lead filter compatibility mode
    if (filterData) {
      // Check if we have actual start/end dates stored
      if (filterData.startDate && filterData.endDate) {
        log('Using stored start/end dates', {
          start: filterData.startDate,
          end: filterData.endDate
        });
        return [moment(filterData.startDate), moment(filterData.endDate)];
      }
      
      // Check if we have a preset selected
      if (filterData.dateLead?.value && filterData.dateLead.value !== 'custom') {
        const today = moment();
        log('Using preset value', filterData.dateLead.value);
        
        switch (filterData.dateLead.value) {
          case 'today':
            return [today.clone(), today.clone()];
          case 'yesterday':
            const yesterday = today.clone().subtract(1, 'days');
            return [yesterday, yesterday];
          case '3DayBefore':
            return [today.clone().subtract(3, 'days'), today.clone()];
          case '1WeekBefore':
            return [today.clone().subtract(1, 'weeks'), today.clone()];
          case '1MonthBefore':
            return [today.clone().subtract(1, 'months'), today.clone()];
          default:
            return null;
        }
      }
    }
    
    return internalRange;
  };
  
  // Handle range picker changes
  const handleRangeChange = (dates: any, dateStrings: [string, string]) => {
    log('Range changed', { dates, dateStrings });
    
    // Update internal state
    setInternalRange(dates);
    
    // If using direct onChange prop
    if (onChange) {
      onChange(dates, dateStrings);
      return;
    }
    
    // Lead filter compatibility mode
    if (dateFilterOnChange && startDate && endDate) {
      if (!dates || !dates[0] || !dates[1]) {
        // Clear dates
        log('Clearing dates');
        dateFilterOnChange(null, "dateLead");
        startDate(undefined);
        endDate(undefined);
        return;
      }
      
      // Set custom date option and update start/end dates
      const customDateOption = { value: "custom", label: "Custom Date" };
      dateFilterOnChange(customDateOption, "dateLead");
      
      // Format dates for lead filter
      const startMoment = dates[0];
      const endMoment = dates[1];
      
      startDate(startMoment.utc().format());
      endDate(endMoment.utc().format());
      
      log('Set custom dates', {
        start: startMoment.format('YYYY-MM-DD'),
        end: endMoment.format('YYYY-MM-DD')
      });
    }
  };
  
  
  // Handle preset clicks (this is where the magic happens)
  const handlePresetClick = (preset: string, range: [Moment, Moment]) => {
    log('Preset clicked', { preset, range: range.map(d => d.format('YYYY-MM-DD')) });
    
    // Update internal state
    setInternalRange(range);
    
    // If using direct onChange prop
    if (onChange) {
      onChange(range, [range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')]);
      return;
    }
    
    // Lead filter compatibility - map presets to expected values
    if (dateFilterOnChange && startDate && endDate) {
      let leadFilterValue = { value: 'custom', label: 'Custom Date' };
      
      // Map preset names to lead filter values
      switch (preset) {
        case 'Today':
          leadFilterValue = { value: 'today', label: 'Today' };
          break;
        case 'Yesterday':
          leadFilterValue = { value: 'yesterday', label: 'Yesterday' };
          break;
        case 'Last 7 Days':
          leadFilterValue = { value: '1WeekBefore', label: '1 Week Before Now' };
          break;
        case 'Last 30 Days':
        case 'Last Month':
          leadFilterValue = { value: '1MonthBefore', label: '1 Month Before Now' };
          break;
        // For other presets, use custom
        default:
          leadFilterValue = { value: 'custom', label: 'Custom Date' };
      }
      
      dateFilterOnChange(leadFilterValue, "dateLead");
      startDate(range[0].utc().format());
      endDate(range[1].utc().format());
      
      log('Updated lead filter', {
        filterValue: leadFilterValue,
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD')
      });
    }
  };
  
  // Define preset ranges based on type - using explicit calculations
  const presetRanges = useMemo(() => {
    const now = new Date();
    
    // Helper to get start of day
    const getStartOfDay = (date: Date): Date => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };
    
    // Helper to get end of day  
    const getEndOfDay = (date: Date): Date => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    
    const baseRanges = {
      'Today': [
        moment(getStartOfDay(now)), 
        moment(getEndOfDay(now))
      ],
      'Yesterday': [
        moment(getStartOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000))), 
        moment(getEndOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000)))
      ],
      'Last 7 Days': [
        moment(getStartOfDay(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000))), 
        moment(getEndOfDay(now))
      ],
      'Last 14 Days': [
        moment(getStartOfDay(new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000))), 
        moment(getEndOfDay(now))
      ],
      'Last 30 Days': [
        moment(getStartOfDay(new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000))), 
        moment(getEndOfDay(now))
      ],
      'This Month': [
        moment(getStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1))), 
        moment(getEndOfDay(now))
      ],
    };
    
    // Debug logging
    if (debugMode) {
      console.log('🗓️ Preset ranges calculated:', {
        today: baseRanges.Today.map(d => d.format('YYYY-MM-DD HH:mm:ss')),
        yesterday: baseRanges.Yesterday.map(d => d.format('YYYY-MM-DD HH:mm:ss')),
        last7Days: baseRanges['Last 7 Days'].map(d => d.format('YYYY-MM-DD HH:mm:ss')),
        last14Days: baseRanges['Last 14 Days'].map(d => d.format('YYYY-MM-DD HH:mm:ss')),
        thisMonth: baseRanges['This Month'].map(d => d.format('YYYY-MM-DD HH:mm:ss')),
        currentDate: now.toISOString(),
        currentDay: now.getDay(), // 0=Sunday, 1=Monday, etc.
        currentMonth: now.getMonth(), // 0=January, 6=July, etc.
      });
      
      // Log the conditional ranges too
      if (presetType === 'lead-filter') {
        const lastSunday = new Date(now);
        lastSunday.setDate(now.getDate() - now.getDay() - 7);
        const lastSaturday = new Date(lastSunday);
        lastSaturday.setDate(lastSunday.getDate() + 6);
        
        console.log('🗓️ Lead filter specific ranges:', {
          lastWeek: [
            moment(getStartOfDay(lastSunday)).format('YYYY-MM-DD HH:mm:ss'),
            moment(getEndOfDay(lastSaturday)).format('YYYY-MM-DD HH:mm:ss')
          ],
          lastMonth: [
            moment(getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1))).format('YYYY-MM-DD HH:mm:ss'),
            moment(getEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0))).format('YYYY-MM-DD HH:mm:ss')
          ]
        });
      }
    }
    
    if (presetType === 'distribution') {
      return {
        ...baseRanges,
        'Last Month': [
          moment(getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1))),
          moment(getEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0)))
        ],
        'Last Quarter': [
          moment(getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 3, 1))),
          moment(getEndOfDay(now))
        ]
      };
    }
    
    if (presetType === 'lead-filter') {
      // Calculate proper last week (previous Sunday to Saturday)
      const lastSunday = new Date(now);
      lastSunday.setDate(now.getDate() - now.getDay() - 7); // Go to last Sunday
      const lastSaturday = new Date(lastSunday);
      lastSaturday.setDate(lastSunday.getDate() + 6); // Add 6 days to get Saturday
      
      return {
        ...baseRanges,
        'Last Week': [
          moment(getStartOfDay(lastSunday)), 
          moment(getEndOfDay(lastSaturday))
        ],
        'Last Month': [
          moment(getStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1))),
          moment(getEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0)))
        ],
      };
    }
    
    return baseRanges;
  }, [presetType]);
  
  // Create enhanced ranges object with click handlers
  const enhancedRanges = useMemo(() => {
    const ranges: any = {};
    Object.entries(presetRanges).forEach(([key, range]) => {
      ranges[key] = range;
    });
    return ranges;
  }, [presetRanges]);
  
  // Get popup container
  const getPopupContainer = () => {
    const container = document.getElementById(containerId);
    return container || document.body;
  };
  
  const currentValue = getCurrentValue();
  
  log('Render', {
    currentValue: currentValue?.map(d => d?.format('YYYY-MM-DD')),
    filterData: filterData?.dateLead,
    showDatePicker: filterData?.showDatePicker
  });
  
  return (
    <div className={`common-range-picker ${className}`} id={containerId}>
      <RangePicker
        value={currentValue}
        onChange={handleRangeChange}
        placeholder={placeholder}
        allowClear={allowClear}
        getPopupContainer={getPopupContainer}
        style={{ minWidth: '300px', width: '100%' }}
        dropdownClassName="common-range-picker-dropdown"
        ranges={enhancedRanges}
        format="DD MMM YYYY"
        // Override the preset click behavior
        onCalendarChange={(dates) => {
          // Handle manual calendar selection
          if (dates && dates[0] && dates[1]) {
            handleRangeChange(dates, [
              dates[0].format('YYYY-MM-DD'),
              dates[1].format('YYYY-MM-DD')
            ]);
          }
        }}
      />
    </div>
  );
};

export default CommonRangePicker;